import React, { useState, useEffect } from 'react';
import { BackgroundSettings as BgSettings, BackgroundMode } from '@/app/types/cad';
import {
  Dialog,
  DialogPortal,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from '@/app/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Switch } from '@/app/components/ui/switch';
import { Button } from '@/app/components/ui/button';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface BackgroundSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: BgSettings;
  onSettingsChange: (settings: BgSettings) => void;
  onPreviewChange: (settings: BgSettings) => void;
}

export function BackgroundSettingsDialog({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
  onPreviewChange,
}: BackgroundSettingsDialogProps) {
  const { t } = useLanguage();
  // Wir halten alle Werte als Strings im lokalen State, damit Fehleingaben (leer, Punkte) möglich sind.
  // Erst beim Senden/Preview wandeln wir zurück in Zahlen.
  const [formData, setFormData] = useState<Record<string, string | boolean | BackgroundMode>>({});

  // Initialisierung: Beim Öffnen Settings in Form-Daten (Strings) umwandeln
  useEffect(() => {
    if (open && settings) {
      const initialData: Record<string, any> = {};
      Object.entries(settings).forEach(([key, value]) => {
        initialData[key] = typeof value === 'number' ? value.toString() : value;
      });
      setFormData(initialData);
    }
  }, [open, settings]);

  // Helper zum Parsen der aktuellen Form-Daten zurück in das BgSettings Format
  const getParsedSettings = (): BgSettings => {
    const parsed: any = { ...settings }; // Basis ist das Original-Objekt für fehlende Keys
    
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof settings[key as keyof BgSettings] === 'number') {
        // Versuchen zu parsen
        const num = parseFloat(value as string);
        parsed[key] = isNaN(num) ? 0 : num; // Fallback auf 0 bei ungültiger Eingabe
      } else {
        parsed[key] = value;
      }
    });
    
    return parsed as BgSettings;
  };

  // Live-Preview Effekt
  useEffect(() => {
    if (open) {
      const currentParsed = getParsedSettings();
      onPreviewChange(currentParsed);
    }
  }, [formData, open]); // Abhängigkeit von formData sorgt für Live-Updates

  // Handler für Text/Zahlen-Inputs
  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Handler für Boolean/Switch/Select
  const handleValueChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleAccept = () => {
    onSettingsChange(getParsedSettings());
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset auf Original-Settings via Preview
    onPreviewChange(settings);
    onOpenChange(false);
  };

  // Helper um den aktuellen Wert sicher als String für das Input-Feld zu bekommen
  const getValue = (key: keyof BgSettings): string => {
    const val = formData[key];
    return val === undefined ? '' : String(val);
  };

  const getBool = (key: keyof BgSettings): boolean => {
    return !!formData[key];
  };

  const getMode = (): BackgroundMode => {
    return (formData['mode'] as BackgroundMode) || 'none';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPortal>
        <DialogPrimitive.Content
          className={cn(
            'fixed right-4 top-20 z-50 w-full max-w-md rounded-lg border bg-white p-6 shadow-lg',
            'max-h-[calc(100vh-6rem)] overflow-y-auto',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          )}
        >
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">{t('common.close')}</span>
          </DialogPrimitive.Close>

          <DialogHeader>
            <DialogTitle>{t('background.title')}</DialogTitle>
            <DialogDescription>
              {t('background.modes.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Globale Einstellungen */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="visible">{t('background.visible')}</Label>
                <Switch
                  id="visible"
                  checked={getBool('visible')}
                  onCheckedChange={(checked) => handleValueChange('visible', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode">{t('background.mode')}</Label>
                <Select
                  value={getMode()}
                  onValueChange={(value) => handleValueChange('mode', value)}
                >
                  <SelectTrigger id="mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="isometric">{t('background.modes.isometric')}</SelectItem>
                    <SelectItem value="cartesian">{t('background.modes.cartesian')}</SelectItem>
                    <SelectItem value="dots">{t('background.modes.dots')}</SelectItem>
                    <SelectItem value="none">{t('background.modes.none')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="backgroundColor">{t('background.color')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="backgroundColor"
                    type="color"
                    value={getValue('backgroundColor')}
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={getValue('backgroundColor')}
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Modus-spezifische Einstellungen */}
            <Tabs value={getMode()} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="isometric">{t('background.modes.isometric').split(' ')[0]}</TabsTrigger>
                <TabsTrigger value="cartesian">{t('background.modes.cartesian').split(' ')[0]}</TabsTrigger>
                <TabsTrigger value="dots">{t('background.modes.dots')}</TabsTrigger>
                <TabsTrigger value="none">{t('background.modes.none')}</TabsTrigger>
              </TabsList>

              <TabsContent value="isometric" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="isometricAngle">{t('background.isometric.angle')}</Label>
                  <Input
                    id="isometricAngle"
                    type="number"
                    value={getValue('isometricAngle')}
                    onChange={(e) => handleChange('isometricAngle', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isometricGridSize">{t('background.isometric.gridSize')}</Label>
                  <Input
                    id="isometricGridSize"
                    type="number"
                    value={getValue('isometricGridSize')}
                    onChange={(e) => handleChange('isometricGridSize', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isometricLineColor">{t('background.isometric.lineColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="isometricLineColor"
                      type="color"
                      value={getValue('isometricLineColor')}
                      onChange={(e) => handleChange('isometricLineColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={getValue('isometricLineColor')}
                      onChange={(e) => handleChange('isometricLineColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isometricLineWidth">{t('background.isometric.lineWidth')}</Label>
                  <Input
                    id="isometricLineWidth"
                    type="number"
                    value={getValue('isometricLineWidth')}
                    onChange={(e) => handleChange('isometricLineWidth', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isometricPointColor">{t('background.isometric.pointColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="isometricPointColor"
                      type="color"
                      value={getValue('isometricPointColor')}
                      onChange={(e) => handleChange('isometricPointColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={getValue('isometricPointColor')}
                      onChange={(e) => handleChange('isometricPointColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="isometricPointSize">{t('background.isometric.pointSize')}</Label>
                  <Input
                    id="isometricPointSize"
                    type="number"
                    value={getValue('isometricPointSize')}
                    onChange={(e) => handleChange('isometricPointSize', e.target.value)}
                  />
                </div>
              </TabsContent>

              <TabsContent value="cartesian" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cartesianGridSize">{t('background.cartesian.gridSize')}</Label>
                  <Input
                    id="cartesianGridSize"
                    type="number"
                    value={getValue('cartesianGridSize')}
                    onChange={(e) => handleChange('cartesianGridSize', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cartesianLineColor">{t('background.cartesian.lineColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cartesianLineColor"
                      type="color"
                      value={getValue('cartesianLineColor')}
                      onChange={(e) => handleChange('cartesianLineColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={getValue('cartesianLineColor')}
                      onChange={(e) => handleChange('cartesianLineColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cartesianLineWidth">{t('background.cartesian.lineWidth')}</Label>
                  <Input
                    id="cartesianLineWidth"
                    type="number"
                    value={getValue('cartesianLineWidth')}
                    onChange={(e) => handleChange('cartesianLineWidth', e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="cartesianShowMinorLines">{t('background.cartesian.showMinor')}</Label>
                  <Switch
                    id="cartesianShowMinorLines"
                    checked={getBool('cartesianShowMinorLines')}
                    onCheckedChange={(checked) =>
                      handleValueChange('cartesianShowMinorLines', checked)
                    }
                  />
                </div>

                {getBool('cartesianShowMinorLines') && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="cartesianMinorDivisions">{t('background.cartesian.minorDivisions')}</Label>
                      <Input
                        id="cartesianMinorDivisions"
                        type="number"
                        value={getValue('cartesianMinorDivisions')}
                        onChange={(e) => handleChange('cartesianMinorDivisions', e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cartesianMinorLineColor">{t('background.cartesian.minorColor')}</Label>
                      <div className="flex gap-2">
                        <Input
                          id="cartesianMinorLineColor"
                          type="color"
                          value={getValue('cartesianMinorLineColor')}
                          onChange={(e) =>
                            handleChange('cartesianMinorLineColor', e.target.value)
                          }
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={getValue('cartesianMinorLineColor')}
                          onChange={(e) =>
                            handleChange('cartesianMinorLineColor', e.target.value)
                          }
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="dots" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="dotsGridSize">{t('background.dots.gridSize')}</Label>
                  <Input
                    id="dotsGridSize"
                    type="number"
                    value={getValue('dotsGridSize')}
                    onChange={(e) => handleChange('dotsGridSize', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dotsColor">{t('background.dots.color')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="dotsColor"
                      type="color"
                      value={getValue('dotsColor')}
                      onChange={(e) => handleChange('dotsColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={getValue('dotsColor')}
                      onChange={(e) => handleChange('dotsColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dotsSize">{t('background.dots.size')}</Label>
                  <Input
                    id="dotsSize"
                    type="number"
                    value={getValue('dotsSize')}
                    onChange={(e) => handleChange('dotsSize', e.target.value)}
                  />
                </div>
              </TabsContent>

              <TabsContent value="none" className="py-8 text-center text-muted-foreground">
                {t('background.modes.description')}
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button type="button" onClick={handleCancel} variant="outline">
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={handleAccept}>
              {t('common.accept')}
            </Button>
          </DialogFooter>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
